// Product Details Manager Class for the Product Details Section
// Handles business logic and state management for individual product details
// Follows the Manager pattern adapted from ProductsManager

import { Manager } from '../../../../../core';
import { logger } from '../../../../../core';

export class ProductDetailsManager extends Manager {
  constructor() {
    super();
    this.product = null;
    this.isLoading = false;
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: 'manager|product-details|init',
        color1: 'cyan',
      },
      'Initializing ProductDetailsSection manager states'
    );

    // Manager is ready but will load product when productId is provided
    logger.log(
      {
        tags: 'manager|product-details|ready',
        color1: 'lime',
      },
      'ProductDetailsSection manager initialized'
    );
  }

  /**
   * Load product details by ID
   */
  async loadProduct(productId) {
    if (!productId) {
      this.product = null;
      return;
    }

    this.isLoading = true;

    try {
      logger.log(
        {
          tags: 'manager|product-details|api',
          color1: 'blue',
        },
        `Loading product details for ID: ${productId}`
      );

      const product = await this.getProductById(productId);
      this.product = product;

      logger.log(
        {
          tags: 'manager|product-details|success',
          color1: 'lime',
        },
        `Loaded product details: ${product?.name || 'Unknown product'}`
      );
    } catch (error) {
      logger.error(
        {
          tags: 'manager|product-details|error',
          color1: 'red',
        },
        'Failed to load product details:',
        error
      );
      this.product = null;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get product details by ID from backend API
   */
  async getProductById(productId) {
    const response = await fetch(`/api/products/${productId}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch product');
    }

    return data.product || null;
  }

  /**
   * Search products from admin API (includes inactive, for add-bundle/related flow)
   */
  async searchProductsFromAdminAPI(query, page = 1, limit = 25, filters = {}) {
    const params = new URLSearchParams({
      q: query || '',
      page: String(page),
      limit: String(limit),
      includeInactive: 'true',
    });

    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
    }

    const response = await fetch(`/api/admin/products/search?${params.toString()}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      return { products: [], total: 0, page, limit };
    }

    return {
      products: data.products || [],
      total: data.total || 0,
      page: data.page || page,
      limit: data.limit || limit,
    };
  }

  /**
   * Search products from backend API (same as ProductsManager)
   */
  async searchProductsFromAPI(query, page = 1, limit = 25, filters = {}) {
    const params = new URLSearchParams({
      q: query || '',
      page: String(page),
      limit: String(limit),
    });

    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
    }

    const response = await fetch(`/api/products/search?${params.toString()}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      return { products: [], total: 0, page, limit };
    }

    return {
      products: data.products || [],
      total: data.total || 0,
      page: data.page || page,
      limit: data.limit || limit,
    };
  }

  /**
   * Update product details by ID
   */
  async updateProduct(productId, updateData) {
    try {
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update product');
      }

      return data.product || null;
    } catch (error) {
      logger.error(
        {
          tags: 'manager|product-details|error',
          color1: 'red',
          includeSource: true,
        },
        'Failed to update product:',
        error
      );
      throw error;
    }
  }

  /**
   * Delete product by ID
   */
  async deleteProduct(productId) {
    const response = await fetch(`/api/admin/products/${productId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to delete product');
    }

    return data;
  }

  /**
   * Create Stripe checkout session for product purchase and redirect to Stripe
   * @param {string} productId - MongoDB product ID
   * @returns {Promise<void>}
   */
  async createProductCheckout(productId) {
    const response = await fetch('/api/stripe/create-product-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ productId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to create checkout session');
    }

    if (!data.url) {
      throw new Error('Checkout session created but no URL returned');
    }

    logger.log(
      { tags: 'manager|product-details|action', color1: 'lime' },
      `Redirecting to Stripe checkout for product ${productId}`
    );

    window.location.href = data.url;
  }

  /**
   * Get current product
   */
  getProduct() {
    return this.product;
  }

  /**
   * Check if currently loading
   */
  getIsLoading() {
    return this.isLoading;
  }
}